/**
 * Module Api Schema - Constants
 * Self-contained module for API documentation and testing
 */

// Permission keys for each feature
export const PERMISSION_KEYS = {
    API_PROJECT: "console:api_project",
    API_ENVIRONMENT: "console:api_environment",
    API_SAVED_REQUEST: "console:api_saved_request",
    API_REQUEST_HISTORY: "console:api_request_history",
    API_PROXY: "console:api_proxy",
    API_REQUEST: "console:api_request",
    API_TEST_CASE: "console:api_test_case"
};

// Page URLs
export const PAGE_URLS = {
    API_PLAYGROUND: "/admin/api-schema/playground",
    API_PROJECT: "/admin/api-schema/project",
    API_PROJECT_DETAIL: "/admin/api-schema/project",
    API_ENVIRONMENT: "/admin/api-schema/environment",
    API_SAVED_REQUEST: "/admin/api-schema/saved-request",
    API_REQUEST_HISTORY: "/admin/api-schema/history"
};

// API Base paths
export const API_ENDPOINTS = {
    PROJECT: "/api/console/control/api-project",
    ENVIRONMENT: "/api/console/control/api-environment",
    SAVED_REQUEST: "/api/console/control/api-saved-request",
    REQUEST_HISTORY: "/api/console/control/api-request-history",
    PROXY: "/api/console/control/api-proxy",
    GLOBAL_CONFIG: "/api/console/control/api-global-config",
    FOLDER: "/api/console/control/api-folder",
    // DB-first entities
    ENDPOINT: "/api/console/control/api-endpoint",
    SCHEMA: "/api/console/control/api-schema",
    TAG: "/api/console/control/api-tag",
    SERVER: "/api/console/control/api-server",
    SECURITY_SCHEME: "/api/console/control/api-security-scheme",
    // Request and Test Case entities
    REQUEST: "/api/console/control/api-request",
    TEST_CASE: "/api/console/control/api-test-case"
};

// Sync strategies
export const SYNC_STRATEGIES = {
    SMART_MERGE: { value: 'SMART_MERGE', label: 'Smart Merge', description: 'Keep user modifications, add new, mark orphaned' },
    FULL_REPLACE: { value: 'FULL_REPLACE', label: 'Full Replace', description: 'Replace all except user-modified items' },
    FORCE_REPLACE: { value: 'FORCE_REPLACE', label: 'Force Replace', description: 'Replace everything (destructive)' }
};

// Entity sync status
export const SYNC_STATUS = {
    SYNCED: { value: 'SYNCED', label: 'Synced', color: '#52c41a' },
    MODIFIED: { value: 'MODIFIED', label: 'Modified', color: '#faad14' },
    ORPHANED: { value: 'ORPHANED', label: 'Orphaned', color: '#ff4d4f' },
    NEW: { value: 'NEW', label: 'New', color: '#1890ff' }
};

export const getEntitySyncStatusColor = (status) => {
    return SYNC_STATUS[status]?.color || '#666';
};

// HTTP Methods
export const HTTP_METHODS = [
    { value: 'GET', label: 'GET', color: '#26a65b' },
    { value: 'POST', label: 'POST', color: '#e88a1a' },
    { value: 'PUT', label: 'PUT', color: '#2d8fd9' },
    { value: 'DELETE', label: 'DELETE', color: '#d62b2b' },
    { value: 'OPTIONS', label: 'OPTIONS', color: '#7a0fd6' },
    { value: 'HEAD', label: 'HEAD', color: '#666666' },
    { value: 'PATCH', label: 'PATCH', color: '#1fa88a' },
    { value: 'TRACE', label: 'TRACE', color: '#666666' }
];

// Body types
export const BODY_TYPES = [
    { value: 'NONE', label: 'None' },
    { value: 'JSON', label: 'JSON' },
    { value: 'FORM_DATA', label: 'Form Data' },
    { value: 'X_WWW_FORM_URLENCODED', label: 'x-www-form-urlencoded' },
    { value: 'RAW', label: 'Raw' },
    { value: 'BINARY', label: 'Binary' }
];

// Project source types
export const PROJECT_SOURCE_TYPES = [
    { value: 'SCANNED', label: 'Auto Scanned' },
    { value: 'IMPORTED', label: 'Imported' },
    { value: 'MANUAL', label: 'Manual' }
];

// Project sync types
export const PROJECT_SYNC_TYPES = [
    { value: 'NONE', label: 'No Sync' },
    { value: 'AUTO_SYNC', label: 'Auto Sync (Internal Schema)' },
    { value: 'AUTO_IMPORT_OPENAPI', label: 'Auto Import OpenAPI' },
    { value: 'AUTO_IMPORT_POSTMAN', label: 'Auto Import Postman' }
];

// Sync status colors
export const SYNC_STATUS_COLORS = {
    SUCCESS: '#52c41a',
    FAILED: '#ff4d4f',
    PENDING: '#faad14'
};

export const getSyncStatusColor = (status) => {
    return SYNC_STATUS_COLORS[status] || '#666';
};

// Response status colors
export const STATUS_COLORS = {
    success: '#52c41a',  // 2xx
    redirect: '#faad14', // 3xx
    clientError: '#ff4d4f', // 4xx
    serverError: '#ff7875' // 5xx
};

export const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return STATUS_COLORS.success;
    if (status >= 300 && status < 400) return STATUS_COLORS.redirect;
    if (status >= 400 && status < 500) return STATUS_COLORS.clientError;
    return STATUS_COLORS.serverError;
};

export const getMethodColor = (method) => {
    const found = HTTP_METHODS.find(m => m.value === method?.toUpperCase());
    return found?.color || '#666';
};

// Case Types for API Endpoint Cases
export const CASE_TYPES = [
    { value: 'DEBUG', label: 'Debug Case', description: 'Quick verification during development' },
    { value: 'TEST', label: 'Test Case', description: 'Comprehensive verification for automation' }
];

// Case Groups (predefined categories)
export const CASE_GROUPS = [
    { value: 'SUCCESS', label: 'Success', color: '#52c41a' },
    { value: 'FAILURE', label: 'Failure', color: '#ff4d4f' },
    { value: 'RECORD_NOT_FOUND', label: 'Record Not Found', color: '#faad14' },
    { value: 'EMPTY_DATA', label: 'Empty Data', color: '#8c8c8c' },
    { value: 'MISSING_PARAMS', label: 'Missing Params', color: '#fadb14' },
    { value: 'WRONG_PARAMS', label: 'Wrong Params', color: '#fadb14' },
    { value: 'LOGGED_IN', label: 'Logged in', color: '#1890ff' },
    { value: 'NOT_LOGGED_IN', label: 'Not Logged in', color: '#722ed1' },
    { value: 'UNAUTHORIZED', label: 'Unauthorized', color: '#ff4d4f' },
    { value: 'FORBIDDEN', label: 'Forbidden', color: '#ff4d4f' },
    { value: 'VALIDATION_ERROR', label: 'Validation Error', color: '#faad14' },
    { value: 'BOUNDARY', label: 'Boundary', color: '#13c2c2' },
    { value: 'SECURITY', label: 'Security', color: '#eb2f96' },
    { value: 'PERFORMANCE', label: 'Performance', color: '#a0d911' },
    { value: 'CUSTOM', label: 'Custom', color: '#666' }
];

// Visibility options
export const VISIBILITY_OPTIONS = [
    { value: 'PRIVATE', label: 'Private', description: 'Only you can see this case' },
    { value: 'PUBLIC', label: 'Public', description: 'All project members can see this case' }
];

export const getCaseGroupColor = (group) => {
    const found = CASE_GROUPS.find(g => g.value === group);
    return found?.color || '#666';
};

export const ASSERTION_TYPES = [
    { value: 'STATUS_CODE', label: 'Status Code', defaultOperator: 'EQUALS', hasTarget: false },
    { value: 'RESPONSE_TIME', label: 'Response Time (ms)', defaultOperator: 'LESS_THAN', hasTarget: false },
    { value: 'JSON_PATH', label: 'JSON Path', defaultOperator: 'EQUALS', hasTarget: true, targetPlaceholder: '$.data.id' },
    { value: 'HEADER', label: 'Response Header', defaultOperator: 'EQUALS', hasTarget: true, targetPlaceholder: 'Content-Type' },
    { value: 'BODY_CONTAINS', label: 'Body Contains', defaultOperator: 'CONTAINS', hasTarget: false }
];

export const ASSERTION_OPERATORS = {
    EQUALS: { label: 'equals', symbol: '=' },
    NOT_EQUALS: { label: 'not equals', symbol: '≠' },
    CONTAINS: { label: 'contains', symbol: '⊃' },
    NOT_CONTAINS: { label: 'not contains', symbol: '⊅' },
    EXISTS: { label: 'exists', symbol: '∃' },
    NOT_EXISTS: { label: 'not exists', symbol: '∄' },
    LESS_THAN: { label: 'less than', symbol: '<' },
    GREATER_THAN: { label: 'greater than', symbol: '>' },
    MATCHES_REGEX: { label: 'matches regex', symbol: '~' }
};

export const OPERATORS_BY_TYPE = {
    STATUS_CODE: ['EQUALS', 'NOT_EQUALS', 'LESS_THAN', 'GREATER_THAN'],
    RESPONSE_TIME: ['LESS_THAN', 'GREATER_THAN', 'EQUALS'],
    JSON_PATH: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'NOT_CONTAINS', 'EXISTS', 'NOT_EXISTS', 'MATCHES_REGEX'],
    HEADER: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'EXISTS', 'NOT_EXISTS'],
    BODY_CONTAINS: ['CONTAINS', 'NOT_CONTAINS']
};

export const TEST_RESULT_STATUS = {
    PASSED: { label: 'Passed', color: '#52c41a', bgColor: '#f6ffed' },
    FAILED: { label: 'Failed', color: '#ff4d4f', bgColor: '#fff2f0' },
    ERROR: { label: 'Error', color: '#faad14', bgColor: '#fffbe6' },
    NOT_RUN: { label: 'Not Run', color: '#8c8c8c', bgColor: '#fafafa' }
};

export const getTestResultColor = (status) => {
    return TEST_RESULT_STATUS[status]?.color || '#8c8c8c';
};

export const getTestResultBgColor = (status) => {
    return TEST_RESULT_STATUS[status]?.bgColor || '#fafafa';
};

// Test Case Categories for filtering (Apidog-style)
export const TEST_CASE_CATEGORIES = [
    { value: 'ALL', label: 'All' },
    { value: 'POSITIVE', label: 'Positive', groups: ['SUCCESS', 'LOGGED_IN', 'EMPTY_DATA'] },
    { value: 'NEGATIVE', label: 'Negative', groups: ['FAILURE', 'RECORD_NOT_FOUND', 'MISSING_PARAMS', 'WRONG_PARAMS', 'NOT_LOGGED_IN', 'UNAUTHORIZED', 'FORBIDDEN', 'VALIDATION_ERROR'] },
    { value: 'BOUNDARY', label: 'Boundary', groups: ['BOUNDARY'] },
    { value: 'SECURITY', label: 'Security', groups: ['SECURITY'] },
    { value: 'OTHER', label: 'Other', groups: ['PERFORMANCE', 'CUSTOM'] }
];

// Map case group to category
export const getCaseCategory = (caseGroup) => {
    if (!caseGroup) return 'OTHER';
    for (const category of TEST_CASE_CATEGORIES) {
        if (category.groups?.includes(caseGroup)) {
            return category.value;
        }
    }
    return 'OTHER';
};
